/**
 * Claim names are read defensively: the deployed auth service is not the same
 * build as the `auth micro service` repo, so anything beyond `sub` and `exp`
 * (which RFC 7519 fixes) is treated as best effort.
 */
export interface AccessTokenPayload {
  /** User id. */
  sub?: string
  userId?: string
  id?: string
  username?: string
  email?: string
  name?: string
  roles?: string[]
  iss?: string
  iat?: number
  /** Expiry, in seconds since epoch. */
  exp?: number
}

/**
 * Decodes the payload of a JWT without verifying it. The signature is verified
 * server-side; this is only used to read `exp` and avoid firing requests with a
 * token we already know is stale.
 */
export function decodeAccessToken(token: string): AccessTokenPayload | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    )

    return JSON.parse(json) as AccessTokenPayload
  } catch {
    return null
  }
}

/**
 * True when the token is missing, or its own `exp` says it is within
 * `skewSeconds` of expiring.
 *
 * A token that is present but whose expiry cannot be read counts as **valid**.
 * This is only a round-trip optimisation — the server is the authority, and the
 * 401 handler will renew if it is wrong. Guessing "expired" instead would fire
 * a refresh before every single request, and since the service rotates the
 * refresh token on each call, that burns a token per request.
 */
export function isAccessTokenExpired(token: string | null, skewSeconds = 30): boolean {
  if (!token) return true

  const payload = decodeAccessToken(token)
  if (!payload?.exp) return false

  return payload.exp * 1000 <= Date.now() + skewSeconds * 1000
}
